import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import api from '@/services/api';

const CurrencyContext = createContext();

const FALLBACK_RATES = {
  USD: 1, EUR: 0.92, GBP: 0.79, JPY: 149.5, CNY: 7.24, KRW: 1320,
  INR: 83.1, SGD: 1.34, TWD: 31.5, VND: 24500, IDR: 15700, THB: 35.2,
  MYR: 4.72, AED: 3.67, SAR: 3.75, AUD: 1.53, CAD: 1.36, CHF: 0.88,
};

const FALLBACK_SYMBOLS = {
  USD: '$', EUR: '\u20ac', GBP: '\u00a3', JPY: '\u00a5', CNY: '\u00a5', KRW: '\u20a9',
  INR: '\u20b9', SGD: 'S$', TWD: 'NT$', VND: '\u20ab', IDR: 'Rp', THB: '\u0e3f',
  MYR: 'RM', AED: 'AED', SAR: 'SAR', AUD: 'A$', CAD: 'C$', CHF: 'CHF',
};

export const CURRENCIES = Object.keys(FALLBACK_RATES).map(c => ({
  code: c, symbol: FALLBACK_SYMBOLS[c] || c, name: c,
}));

export const CurrencyProvider = ({ children }) => {
  const [currency, setCurrency] = useState(() => localStorage.getItem('1data_currency') || 'USD');
  const [rates, setRates] = useState(FALLBACK_RATES);
  const [symbols, setSymbols] = useState(FALLBACK_SYMBOLS);

  // Fetch cached rates from backend on mount
  useEffect(() => {
    api.get('/currency/rates').then(res => {
      if (res.data?.rates) setRates(res.data.rates);
      if (res.data?.symbols) setSymbols(res.data.symbols);
    }).catch(() => {});
  }, []);

  const changeCurrency = useCallback((code) => {
    setCurrency(code);
    localStorage.setItem('1data_currency', code);
  }, []);

  const convert = useCallback((amountUSD, decimals = 2) => {
    if (amountUSD == null || isNaN(amountUSD)) return '\u2014';
    const converted = amountUSD * (rates[currency] || 1);
    return converted.toFixed(decimals);
  }, [currency, rates]);

  const format = useCallback((amountUSD, decimals = 2) => {
    if (amountUSD == null || isNaN(amountUSD)) return '\u2014';
    const converted = amountUSD * (rates[currency] || 1);
    const sym = symbols[currency] || currency;
    if (Math.abs(converted) >= 1e6) return `${sym}${(converted / 1e6).toFixed(1)}M`;
    if (Math.abs(converted) >= 1e3) return `${sym}${(converted / 1e3).toFixed(1)}K`;
    return `${sym}${converted.toFixed(decimals)}`;
  }, [currency, rates, symbols]);

  const formatFull = useCallback((amountUSD, decimals = 2) => {
    if (amountUSD == null || isNaN(amountUSD)) return '\u2014';
    const converted = amountUSD * (rates[currency] || 1);
    const sym = symbols[currency] || currency;
    return `${sym}${converted.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}`;
  }, [currency, rates, symbols]);

  const symbol = symbols[currency] || currency;

  return (
    <CurrencyContext.Provider value={{ currency, symbol, changeCurrency, convert, format, formatFull, rates, symbols }}>
      {children}
    </CurrencyContext.Provider>
  );
};

export const useCurrency = () => {
  const ctx = useContext(CurrencyContext);
  if (!ctx) throw new Error('useCurrency must be used within CurrencyProvider');
  return ctx;
};
